import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useAuth';
import { getBoxTemplate } from '../constants/industryTemplates';
import { PostFormContent } from '../components/PostFormContent';
import { ThanksPostsPanel } from '../components/ThanksPostsPanel';
import { AppColors } from '../theme/colors';
import { useIsDesktop } from '../hooks/useMediaQuery';

export const PostPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { company, loading: companyLoading } = useCompany(user?.uid || null);
  const isDesktop = useIsDesktop();
  
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  const initialPostTypeFromLocation = useMemo(() => {
    // 画面統一のため、基本はクエリ（/post/new?type=thanks）で受け取る
    const params = new URLSearchParams(location.search);
    const typeFromQuery = params.get('type');
    if (typeFromQuery === 'voice' || typeFromQuery === 'thanks' || typeFromQuery === 'theme') {
      return typeFromQuery;
    }

    // 互換性のため、古い state 渡しにも対応（ただしリロードで消える）
    const state = location.state as { initialPostType?: 'voice' | 'thanks' | 'theme' } | null;
    if (state?.initialPostType) return state.initialPostType;

    return 'voice';
  }, [location.search, location.state]);

  const handleClose = () => {
    // 内容がある場合のみダイアログを表示
    if (hasContent) {
      setDiscardDialogOpen(true);
    } else {
      navigate('/home');
    }
  };

  const handleDiscard = () => {
    setDiscardDialogOpen(false);
    navigate('/home');
  };

  const handlePostCreated = () => {
    navigate('/home');
  };

  const handleContentChange = (hasContent: boolean) => {
    setHasContent(hasContent);
  };

  // 「ホームへ戻る」ボタン押下時：モバイルでウェルカムモーダルを再表示させるためにフラグをリセットしてからホームへ戻る
  const handleCancelToHome = () => {
    try {
      sessionStorage.removeItem('welcome_dismissed');
      sessionStorage.setItem('welcome_force_open', 'true');
    } catch (e) {
      // sessionStorage が使えない環境でも致命的ではないので握りつぶす
      console.error('Failed to set welcome flags:', e);
    }
    navigate('/home');
  };

  // ボックス名と説明文を取得（業種テンプレートから動的に解決）
  const boxInfo = getBoxTemplate(company?.industryType, initialPostTypeFromLocation);

  // ローディング状態の表示
  if (authLoading || companyLoading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        bgcolor: AppColors.surface 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // 会社情報が取得できない場合
  if (!company) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        bgcolor: AppColors.surface,
        flexDirection: 'column',
        p: 3
      }}>
        <Typography variant="h6" sx={{ mb: 2, color: AppColors.error }}>
          会社情報が取得できません
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, textAlign: 'center' }}>
          会社情報の取得に失敗しました。ページを再読み込みするか、管理者にお問い合わせください。
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{ bgcolor: AppColors.primary }}
        >
          ページを再読み込み
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: AppColors.surface, width: '100%' }}>
      {/* ヘッダー */}
      <AppBar
        position="static"
        sx={{
          bgcolor: 'white',
          boxShadow: 'none',
          borderBottom: '1px solid #e0e0e0',
          width: '100%',
        }}
      >
        <Toolbar sx={{ width: '100%', flexDirection: isDesktop ? 'row' : 'column', alignItems: isDesktop ? 'center' : 'flex-start', py: isDesktop ? 1 : 1.5 }}>
          <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, flexDirection: isDesktop ? 'row' : 'column', alignItems: isDesktop ? 'center' : 'flex-start', width: '100%' }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                color: 'black',
                fontWeight: 600,
              }}
            >
              新規投稿
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexDirection: isDesktop ? 'row' : 'column', alignItems: isDesktop ? 'center' : 'flex-start' }}>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  color: AppColors.textPrimary,
                  fontWeight: 600,
                  fontSize: isDesktop ? 16 : 15,
                }}
              >
                {boxInfo.name}
              </Typography>
              <Typography
                variant="body2"
                component="span"
                sx={{
                  color: AppColors.textSecondary,
                  fontSize: isDesktop ? 14 : 13,
                }}
              >
                {boxInfo.description}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* メインコンテンツ */}
      <Box sx={{ 
        width: '100%', 
        maxWidth: isDesktop ? '1100px' : '100%',
        mx: isDesktop ? 'auto' : 0,
      }}>
        {initialPostTypeFromLocation === 'thanks' ? (
          <Box
            sx={{
              display: isDesktop ? 'grid' : 'flex',
              gridTemplateColumns: isDesktop ? '1fr 420px' : undefined,
              gap: 2,
              flexDirection: isDesktop ? undefined : 'column',
              p: 2,
              pb: isDesktop ? 2 : 0,
            }}
          >
            <Box sx={{ width: '100%' }}>
              <PostFormContent 
                onPostCreated={handlePostCreated}
                onCancel={handleCancelToHome}
                onContentChange={handleContentChange}
                initialPostType={initialPostTypeFromLocation}
                showPostTypeSelector={false}
              />
            </Box>
            <Box sx={{ width: '100%' }}>
              {company?.id ? <ThanksPostsPanel companyId={company.id} /> : null}
            </Box>
          </Box>
        ) : (
          <PostFormContent 
            onPostCreated={handlePostCreated}
            onCancel={handleCancelToHome}
            onContentChange={handleContentChange}
            initialPostType={initialPostTypeFromLocation}
            showPostTypeSelector={false}
          />
        )}
      </Box>

      {/* 破棄確認ダイアログ */}
      <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)}>
        <DialogTitle>投稿を破棄しますか？</DialogTitle>
        <DialogContent>
          <DialogContentText>
            入力した内容は保存されません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleDiscard}
            color="error"
          >
            破棄する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
